import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Check, Loader, Mail, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';

const PartnerWillkommenPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, timeout
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 30; // 30 * 2s = 60s

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const token = searchParams.get('token');
    
    // Token present? Redirect to Set Password page (as PATH parameter)
    if (token) {
      navigate(`/partner/set-password/${token}`);
      return;
    }
    
    // No session_id? Redirect to abo page
    if (!sessionId) {
      navigate('/partner/abo');
      return;
    }

    // Stripe Success Flow
    pollPaymentStatus(sessionId);
  }, [searchParams, navigate]);

  const pollPaymentStatus = async (sessionId) => {
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stripe/checkout-status/${sessionId}`);
      const data = await response.json();

      if (data.payment_status === 'paid') {
        setStatus('success');
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(() => pollPaymentStatus(sessionId), 2000);
    } catch (error) {
      console.error('Status check failed:', error);
      setAttempts(prev => prev + 1);
      setTimeout(() => pollPaymentStatus(sessionId), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Minimal Header - nur Logo */}
      <div className="bg-white border-b border-gray-200 py-4">
        <div className="max-w-7xl mx-auto px-6">
          <img 
            src="https://customer-assets.emergentagent.com/job_gutachten-preview/artifacts/gusc9edr_image.png" 
            alt="Dein Gutachten Direkt" 
            className="h-8 w-auto"
          />
        </div>
      </div>
      
      <main className="flex-1 flex items-center justify-center py-20 px-6">
        <div className="max-w-2xl mx-auto px-6 lg:px-8 w-full">
          {status === 'checking' && (
            <div className="text-center bg-white rounded-2xl p-12 shadow-lg">
              <Loader className="w-16 h-16 text-brand-green mx-auto mb-6 animate-spin" />
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                Zahlung wird bestätigt...
              </h1>
              <p className="text-gray-600 mb-2">
                Wir richten deinen Zugang ein. Bitte einen Moment Geduld.
              </p>
              <p className="text-sm text-gray-500 mb-8">
                Das dauert meist nur wenige Sekunden, in seltenen Fällen bis zu 1 Minute. Bitte Tab offen lassen.
              </p>
              
              <div className="flex gap-3 justify-center text-sm">
                <button
                  onClick={() => navigate('/partner/abo')}
                  className="text-gray-600 hover:text-gray-900 underline"
                >
                  Zurück zur Abo-Seite
                </button>
                <span className="text-gray-400">|</span>
                <button
                  onClick={() => navigate('/portal/dashboard')}
                  className="text-gray-600 hover:text-gray-900 underline"
                >
                  Zum Partner-Login
                </button>
              </div>
            </div>
          )}

          {status === 'success' && (
            <div className="bg-white border border-gray-200 rounded-2xl p-12 shadow-lg text-center">
              <div className="w-20 h-20 bg-brand-green/10 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-brand-green/15">
                <Check className="w-10 h-10 text-brand-green" />
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                Zahlung ist eingegangen!
              </h1>
              <div className="flex items-center justify-center gap-3 mb-6">
                <Mail className="w-6 w-6 text-brand-green" />
                <p className="text-xl text-gray-600">
                  Wir haben dir eine Willkommensmail gesendet
                </p>
              </div>
              <p className="text-gray-600 mb-8">
                Klicke auf den Link in der E-Mail, um deinen Zugang einzurichten und loszulegen.
              </p>
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 mb-8">
                <p className="text-sm text-gray-700">
                  <strong>Kein Link erhalten?</strong> Prüfe deinen Spam-Ordner oder melde dich direkt im Portal an.
                </p>
              </div>
              <Button 
                onClick={() => navigate('/portal/dashboard')}
                size="lg"
                className="bg-brand-green hover:bg-brand-green-dark text-white px-8"
              >
                Zum Partner-Portal
              </Button>
            </div>
          )}

          {status === 'timeout' && (
            <div className="bg-white border border-yellow-200 rounded-3xl p-12 shadow-lg text-center">
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                Prüfung läuft noch
              </h1>
              <p className="text-gray-600 mb-6">
                Wir prüfen noch Ihre Zahlung. Bitte aktualisieren Sie die Seite oder kontaktieren Sie uns.
              </p>
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                className="border-2 border-gray-300"
              >
                Status erneut prüfen
              </Button>
            </div>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default PartnerWillkommenPage;
